/**
 * <transformation-tabs> — swipeable before/after tabs.
 *
 * Markup contract (see sections/transformation-stories.liquid):
 *   <transformation-tabs>
 *     <input type="radio" data-ts-tabs-radio data-index="0"> …
 *     <div data-ts-tabs-labels> <label data-index="0">…</label> … </div>
 *     <div data-ts-tabs-track>
 *       <div data-ts-tabs-panel>…</div> …
 *     </div>
 *   </transformation-tabs>
 *
 * The radios drive the active label highlight in CSS. On mobile the track is a
 * scroll-snap carousel; this component keeps the two in sync in both directions.
 * On desktop (>=1080px) the track is a static grid and the component stays dormant.
 */
if (!customElements.get('transformation-tabs')) {
  class TransformationTabs extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ts-tabs-track]');
      if (!this.track) return;

      this.panels = Array.from(this.track.querySelectorAll('[data-ts-tabs-panel]'));
      this.radios = Array.from(this.querySelectorAll('[data-ts-tabs-radio]'));
      this.labelsRow = this.querySelector('[data-ts-tabs-labels]');
      this.labels = this.labelsRow ? Array.from(this.labelsRow.querySelectorAll('[data-index]')) : [];
      this.activeIndex = this.radios.findIndex((radio) => radio.checked);
      if (this.activeIndex === -1) this.activeIndex = 0;

      if (this.panels.length < 2) return;

      this.onRadioChange = (event) => {
        if (this.desktopQuery.matches) return;
        const radio = event.target.closest('[data-ts-tabs-radio]');
        if (!radio) return;
        const index = Number(radio.dataset.index);
        if (Number.isNaN(index) || index === this.activeIndex) return;
        this.activeIndex = index;
        this.scrollTrackTo(index);
        this.centerLabel(index);
      };
      this.addEventListener('change', this.onRadioChange);

      this.desktopQuery = window.matchMedia('(min-width: 1080px)');
      this.onBreakpointChange = () => this.syncMode();
      this.desktopQuery.addEventListener('change', this.onBreakpointChange);

      this.syncMode();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onRadioChange);
      this.desktopQuery?.removeEventListener('change', this.onBreakpointChange);
      this.observer?.disconnect();
    }

    /** Watch the track on mobile, stay dormant on desktop. */
    syncMode() {
      if (this.desktopQuery.matches) {
        this.observer?.disconnect();
        this.observer = null;
        return;
      }

      if (!this.observer) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const index = this.panels.indexOf(entry.target);
              if (index !== -1) this.setActive(index);
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.panels.forEach((panel) => this.observer.observe(panel));
      }
    }

    /** Scroll the track so the panel at `index` is in view. */
    scrollTrackTo(index) {
      const panel = this.panels[index];
      if (!panel) return;

      const delta = panel.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: 'smooth' });
    }

    /** Check the matching radio and bring its label into view. */
    setActive(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;

      // No `change` event here — the radio is the state, not the trigger.
      const radio = this.radios[index];
      if (radio) radio.checked = true;

      this.centerLabel(index);
    }

    /**
     * Centre the active label in the horizontally scrollable label row.
     * Done by hand rather than with `scrollIntoView`, which would also scroll the page.
     * No clamping needed: the browser caps scrollLeft at [0, scrollWidth - clientWidth],
     * so the first and last labels simply come to rest against the ends.
     */
    centerLabel(index) {
      const label = this.labels[index];
      if (!label || !this.labelsRow) return;

      const rowRect = this.labelsRow.getBoundingClientRect();
      const labelRect = label.getBoundingClientRect();
      const delta = labelRect.left + labelRect.width / 2 - (rowRect.left + rowRect.width / 2);

      this.labelsRow.scrollTo({ left: this.labelsRow.scrollLeft + delta, behavior: 'smooth' });
    }
  }

  customElements.define('transformation-tabs', TransformationTabs);
}
